import { Card, Divider, Flex, Select, Space, Typography } from "antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../../../../api";
import { GridMinMax } from "../../../../assets/styles/GlobalStyle";
import Loader from "../../../../components/Loader";
import { TranslationsField } from "../../../../domain/types";
import { useAppSelector } from "../../../../store";
import { AppState } from "../../../../store/appSlice";
import AdminLangSwicher from "../../../admin_components/AdminLangSwicher";

const { Text } = Typography;

const AdminPetManagePages = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useAppSelector((root) => root.app);
  const { field_Language } = useAppSelector(
    (data: { app: AppState }) => data.app
  );

  // page slugs
  const pageSlugs: {
    id: number;
    slug: string;
    text: string;
    translations: TranslationsField<{ text: string }>;
  }[] = [
    {
      id: 1,
      slug: "pet-dog-or-cat",
      text: "ძაღლი თუ კატა?",
      translations: {
        en: {
          text: "Who do you buy insurance for",
        },
        ge: {
          text: "ძაღლი თუ კატა?",
        },
      },
    },
    {
      id: 2,
      slug: "pet-info",
      text: "შინაური ცხოველის ინფორმაცია",
      translations: {
        en: {
          text: "Pet information",
        },
        ge: {
          text: "შინაური ცხოველის ინფორმაცია",
        },
      },
    },
    {
      id: 3,
      slug: "pet-insurance-start-date",
      text: "სადაზღვევო პერიოდი",
      translations: {
        en: {
          text: "Insurance period",
        },
        ge: {
          text: "სადაზღვევო პერიოდი",
        },
      },
    },
    {
      id: 4,
      slug: "pet-trust-us-or-yourself",
      text: "შეთავაზების ტიპი",
      translations: {
        en: {
          text: "Offer type",
        },
        ge: {
          text: "შეთავაზების ტიპი",
        },
      },
    },
    {
      id: 5,
      slug: "pet-which-offer",
      text: "შეთავაზებები",
      translations: {
        en: {
          text: "Offers",
        },
        ge: {
          text: "შეთავაზებები",
        },
      },
    },
    {
      id: 6,
      slug: "pet-best-offer",
      text: "საუკეთესო შეთავაზება",
      translations: {
        en: {
          text: "Best offer",
        },
        ge: {
          text: "საუკეთესო შეთავაზება",
        },
      },
    },
    {
      id: 7,
      slug: "pet-your-personal-info",
      text: "დამზღვევის ინფორმაცია",
      translations: {
        en: {
          text: "Insured's Information",
        },
        ge: {
          text: "დამზღვევის ინფორმაცია",
        },
      },
    },
    {
      id: 8,
      slug: "pet-check-info",
      text: "ინფორმაციის გადამოწმება",
      translations: {
        en: {
          text: "Check information",
        },
        ge: {
          text: "ინფორმაციის გადამოწმება",
        },
      },
    },
    {
      id: 9,
      slug: "pet-your-contact-info",
      text: "საკონტაქტო ინფორმაცია",
      translations: {
        en: {
          text: "Contact info",
        },
        ge: {
          text: "საკონტაქტო ინფორმაცია",
        },
      },
    },
  ];

  // selected slug
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);

  // get pet flow
  const { data: pageTextArry, isLoading: isPageTextArrayLoading } =
    api.useGetTplFlowPageTextsBySlugQuery(selectedSlug as string, {
      skip: !selectedSlug && true,
    });

  const mainLoading = isPageTextArrayLoading;

  return mainLoading ? (
    <Loader />
  ) : (
    <>
      <Flex gap={20} justify="space-between" align="flex-end">
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text>{t("choosePage")}</Text>
          <Select
            size="large"
            style={{
              width: "100%",
            }}
            placeholder={t("choosePage")}
            value={selectedSlug || undefined}
            onChange={(value) => setSelectedSlug(value)}
            options={[
              ...pageSlugs.map((item) => ({
                value: item.slug,
                label: item?.translations?.[lang]?.text,
              })),
            ]}
          />
        </Space>

        <AdminLangSwicher />
      </Flex>

      {pageTextArry && (
        <>
          <Divider />

          <GridMinMax align="stretch">
            {pageTextArry?.translations && (
              <Card
                hoverable
                onClick={() =>
                  navigate(`/manage-pet-pages-edit/${selectedSlug}`)
                }
                style={{ cursor: "pointer" }}
              >
                {pageTextArry?.translations?.[field_Language]?.title && (
                  <Space direction="vertical">
                    <Text type="secondary">{t("title")}</Text>
                    <Text>
                      {pageTextArry?.translations?.[field_Language]?.title}
                    </Text>
                  </Space>
                )}

                <Divider variant="dashed" />

                {pageTextArry?.translations?.[field_Language]?.stepName && (
                  <Space direction="vertical">
                    <Text type="secondary">{t("stepName")}</Text>
                    <Text>
                      {pageTextArry?.translations?.[field_Language]?.stepName}
                    </Text>
                  </Space>
                )}
              </Card>
            )}

            {pageTextArry?.listItems?.map((item, index) => (
              <Card
                hoverable
                key={index}
                onClick={() =>
                  navigate(`/manage-pet-pages-edit/${selectedSlug}/${item?.id}`)
                }
              >
                {item?.translations?.[field_Language]?.title && (
                  <Space direction="vertical">
                    <Text type="secondary">{t("title")}</Text>
                    <Text>{item?.translations?.[field_Language]?.title}</Text>
                  </Space>
                )}

                <Divider variant="dashed" />

                {item?.translations?.[field_Language]?.body && (
                  <>
                    <Space direction="vertical">
                      <Text type="secondary">{t("body")}</Text>
                      <Text>{item?.translations?.[field_Language]?.body}</Text>
                    </Space>
                  </>
                )}
              </Card>
            ))}
          </GridMinMax>
        </>
      )}
    </>
  );
};

export default AdminPetManagePages;
